import { useState, useEffect, useCallback } from 'react';
import { useActiveStore } from '../context/ActiveStoreContext';
import { PERMISSION_CONFIG, UserPermission } from './usePermissionCheck';
import axios from '../lib/axios';

interface UsePermissionsReturn {
  permissions: UserPermission;
  isLoading: boolean;
  error: string | null;
  checkPermission: (permission: keyof UserPermission) => boolean;
  hasAnyPermission: (permissions: (keyof UserPermission)[]) => boolean;
  hasAllPermissions: (permissions: (keyof UserPermission)[]) => boolean;
  refreshPermissions: () => Promise<void>;
}

export function useEnhancedPermissions(): UsePermissionsReturn {
  const { activeStore } = useActiveStore();
  const [permissions, setPermissions] = useState<UserPermission>({
    sight_access: false,
    billing_access: false,
    settings_access: false,
    integrations_access: false,
    scaleboard_access: false,
    support_access: false,
    isight_access: false,
    notification_access: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPermissions = useCallback(async (): Promise<void> => {
    // console.log('🔍 loadPermissions called, activeStore:', activeStore);
    // console.log('🔍 activeStore.permissions:', activeStore?.permissions);
    
    if (!activeStore) {
      // console.log('❌ No activeStore, setting all permissions to false');
      setPermissions({
        sight_access: false,
        billing_access: false,
        settings_access: false,
        integrations_access: false,
        scaleboard_access: false,
        support_access: false,
        isight_access: false,
        notification_access: false,
      });
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // First, check if permissions are already in the activeStore data
      if (activeStore.permissions) {
        // console.log('✅ Permissions found in activeStore:', activeStore.permissions);
        const parsedPermissions: UserPermission = {
          sight_access: activeStore.permissions['sight_access'] ?? false,
          billing_access: activeStore.permissions['billing_access'] ?? false,
          settings_access: activeStore.permissions['settings_access'] ?? false,
          integrations_access: activeStore.permissions['integrations_access'] ?? false,
          scaleboard_access: activeStore.permissions['scaleboard_access'] ?? false,
          support_access: activeStore.permissions['support_access'] ?? false,
          isight_access: activeStore.permissions['isight_access'] ?? false,
          notification_access: activeStore.permissions['notification_access'] ?? false,
        };
        // console.log('🔍 Final permissions set from store:', parsedPermissions);
        setPermissions(parsedPermissions);
        return;
      }

      const token = localStorage.getItem('token');
      // console.log('🔍 Token found:', !!token);
      if (!token) throw new Error('No authentication token found');

      const apiUrl = `/api/permissions/stores/${activeStore.id}`;
      // console.log('🌐 Fetching permissions from API:', apiUrl);

      const response = await axios.get(apiUrl);
      const data = response.data;
      // console.log('🔍 API Response:', data);

      if (!data.success) {
        throw new Error(data.message || 'Failed to load permissions');
      }

      // Parse permissions from JSON
      const permissionData = data.permissions ? JSON.parse(data.permissions) : {};
      // console.log('🔍 Parsed permission data:', permissionData);

      const parsedPermissions: UserPermission = {
        sight_access: permissionData['sight_access'] ?? false,
        billing_access: permissionData['billing_access'] ?? false,
        settings_access: permissionData['settings_access'] ?? false,
        integrations_access: permissionData['integrations_access'] ?? false,
        scaleboard_access: permissionData['scaleboard_access'] ?? false,
        support_access: permissionData['support_access'] ?? false,
        isight_access: permissionData['isight_access'] ?? false,
        notification_access: permissionData['notification_access'] ?? false,
      };
      
      // console.log('🔍 Permission API Response Analysis:');
      // console.log('  - Raw permissionData:', permissionData);
      // console.log('  - Parsed permissions:', parsedPermissions);
      // console.log('  - Data type:', typeof permissionData);
      // console.log('  - Data keys:', Object.keys(permissionData));
      
      setPermissions(parsedPermissions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
      console.error('❌ Failed to load permissions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [activeStore]);

  // Load permissions when active store changes
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Listen for store change events
  useEffect(() => {
    const handleStoreChange = () => {
      // console.log('Store change detected, refreshing permissions...');
      loadPermissions();
    };

    window.addEventListener('storeChanged', handleStoreChange);
    return () => window.removeEventListener('storeChanged', handleStoreChange);
  }, [loadPermissions]);

  const checkPermission = useCallback((permission: keyof UserPermission): boolean => {
    const result = permissions[permission] || false;
    // console.log(`🔍 checkPermission(${permission}):`, result, 'all permissions:', permissions);
    
    // Get user role from active store for debugging
    const activeStoreData = activeStore as any;
    const userRole = activeStoreData?.user_role || 'unknown';
    // console.log(`👤 User role: ${userRole} for store: ${activeStoreData?.shop}`);
    
    // If result is false, show which permission is blocking access
    if (!result && permission) {
      // console.warn(`⚠️ Permission DENIED for "${permission}" → User cannot access this feature`);
      // console.warn(`🔍 Debug info:`, {
      //   permission,
      //   result,
      //   userRole,
      //   permissions,
      //   activeStore: activeStoreData
      // });
    } else {
      // console.log(`✅ Permission GRANTED for "${permission}"`);
    }
    
    return result;
  }, [permissions]);

  const hasAnyPermission = useCallback((requiredPermissions: (keyof UserPermission)[]): boolean => {
    return requiredPermissions.some(perm => permissions[perm] || false);
  }, [permissions]);

  const hasAllPermissions = useCallback((requiredPermissions: (keyof UserPermission)[]): boolean => {
    return requiredPermissions.every(perm => permissions[perm] || false);
  }, [permissions]);

  const refreshPermissions = useCallback(async (): Promise<void> => {
    await loadPermissions();
  }, [loadPermissions]);

  return {
    permissions,
    isLoading,
    error,
    checkPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions,
  };
}

// Helper function to get permission config
export function getPermissionConfig(permission: keyof UserPermission) {
  return PERMISSION_CONFIG.find(p => p.id === permission);
}

// Helper function to check if route is accessible
export function isRouteAccessible(route: string, checkPermission: (permission: keyof UserPermission) => boolean): boolean {
  const permission = getPermissionByPage(route);
  if (!permission) return true; // Routes without permission requirements are accessible
  return checkPermission(permission);
}

// Helper function to get permission by page/route
function getPermissionByPage(page: string): keyof UserPermission | null {
  const routeMap: Record<string, keyof UserPermission | null> = {
    '/profile': null, // Profile should be accessible to all authenticated users
    '/sight': 'sight_access',
    '/settings': 'settings_access',
    '/billing': 'billing_access',
    '/invoices': 'billing_access',
    '/credits-history': 'billing_access',
    '/integrations': 'integrations_access',
    '/agency': 'sight_access',
    '/scaleboard': 'scaleboard_access',
    '/scaleboard/marketing': 'scaleboard_access',
    '/scaleboard/product': 'scaleboard_access',
    '/scaleboard/customer': 'scaleboard_access',
    '/scaleboard/isight-ai': 'scaleboard_access',
    '/support': null, // Hide support menu completely
    '/isights': 'isight_access',
    '/manual-inputs': 'sight_access',
  };

  return routeMap[page] || null;
}
